import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { GLOSARIO, type ClaveGlosario } from '@/lib/glosario';
import { cn } from '@/lib/cn';

/** Separación entre el disparador y el globo, y margen mínimo contra el borde. */
const HUECO = 8;
const BORDE = 12;
const ANCHO = 320;

/**
 * Explica un término del producto al pasar el mouse (o al llegar con el teclado).
 *
 * ⚠️ Se dibuja en un **portal con posición fija**, no como hijo del elemento que lo abre.
 * Adentro de una tabla las dos formas se ven igual hasta que hay datos: un globo `absolute`
 * lo **recorta el `overflow-x` del contenedor** de la tabla, y aunque no lo recortara, las
 * filas de abajo van después en el DOM y pintan su fondo **encima** — el gotcha del popover
 * tapado que este monorepo ya tiene anotado, y que ningún `z-index` sobre el globo arregla
 * porque el problema es de orden de pintado entre hermanos.
 *
 * Se abre con `mouseenter` y con `focus`: si sólo respondiera al mouse, la ayuda no existiría
 * ni para el teclado ni para quien entre desde el teléfono.
 *
 * ⚠️ Y el portal NO siempre va al `<body>`: si la ayuda vive adentro de un `<dialog>` abierto
 * con `showModal()`, el globo tiene que dibujarse **adentro de ese diálogo**. Un diálogo
 * modal se promueve al *top layer*, que está fuera del orden de apilamiento normal y le gana
 * a CUALQUIER `z-index` — el globo en el `body` con `z-50` quedaba tapado por la ficha del
 * producto. Medido con `elementFromPoint`: en el centro del globo se veía el gráfico de la
 * ficha, no el globo. Es el gotcha del monorepo sobre el top layer.
 */
export function Ayuda({
  termino,
  className,
  children,
}: {
  termino: ClaveGlosario;
  className?: string;
  /** Con hijos, el disparador es el contenido; sin hijos, es un signo de interrogación. */
  children?: React.ReactNode;
}) {
  const { titulo, texto } = GLOSARIO[termino];
  const id = useId();
  const disparador = useRef<HTMLButtonElement>(null);
  const globo = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; abajo: boolean } | null>(null);

  const ubicar = useCallback(() => {
    const el = disparador.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // El globo va arriba salvo que no entre: ahí baja. Se decide contra el viewport real,
    // que es lo que ve el usuario, y no contra el alto del documento.
    const abajo = r.top < window.innerHeight / 2;
    const left = Math.min(
      Math.max(BORDE, r.left + r.width / 2 - ANCHO / 2),
      window.innerWidth - ANCHO - BORDE,
    );
    setPos({ top: abajo ? r.bottom + HUECO : r.top - HUECO, left, abajo });
  }, []);

  const cerrar = useCallback(() => setPos(null), []);

  /** Dónde dibujar el globo: el diálogo modal que lo contenga, o el `<body>`. */
  const contenedor = () => disparador.current?.closest('dialog[open]') ?? document.body;

  /**
   * Segunda pasada, ya con el globo pintado: se lo mete adentro de la pantalla.
   *
   * ⚠️ Hace falta porque el alto del globo NO se conoce antes de dibujarlo —depende de
   * cuánto texto tenga el término— así que la primera cuenta sólo puede acotar el eje
   * horizontal. Sin esto, un encabezado cerca del borde de abajo abría el globo con la
   * mitad fuera de la pantalla: medido, 1.064px de alto en un viewport de 1.000.
   */
  useLayoutEffect(() => {
    const el = globo.current;
    if (!pos || !el) return;
    const r = el.getBoundingClientRect();
    const desborde = r.bottom - (window.innerHeight - BORDE);
    const falta = BORDE - r.top;
    if (desborde > 0.5) setPos({ ...pos, top: pos.top - desborde });
    else if (falta > 0.5) setPos({ ...pos, top: pos.top + falta });
    // `pos.top` fuera de las dependencias a propósito: se corrige UNA vez por apertura, y
    // volver a mirarlo dispararía el efecto con el valor ya corregido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos?.left, pos?.abajo]);

  /**
   * Si la página se mueve con el globo abierto, se REUBICA; y si el disparador se fue de la
   * pantalla, ahí sí se cierra.
   *
   * ⚠️ La primera versión cerraba ante cualquier scroll, que suena razonable y rompe el
   * acceso por teclado: llegar con Tab a un control que está fuera de vista hace que el
   * navegador scrollee para enfocarlo, y ese scroll cerraba el globo que el foco acababa de
   * abrir. O sea que con el teclado la ayuda no se veía nunca. Apareció porque el navegador
   * de la prueba scrollea igual que un usuario con Tab.
   */
  useLayoutEffect(() => {
    if (!pos) return;
    const alMoverse = () => {
      const el = disparador.current;
      if (!el) return cerrar();
      const r = el.getBoundingClientRect();
      const visible = r.bottom > 0 && r.top < window.innerHeight;
      visible ? ubicar() : cerrar();
    };
    window.addEventListener('scroll', alMoverse, true);
    window.addEventListener('resize', alMoverse);
    return () => {
      window.removeEventListener('scroll', alMoverse, true);
      window.removeEventListener('resize', alMoverse);
    };
    // `pos` no va en las dependencias: cambia en cada reubicación y volvería a montar los
    // listeners en bucle. Alcanza con saber si hay globo abierto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos != null, cerrar, ubicar]);

  return (
    <>
      <button
        ref={disparador}
        type="button"
        aria-label={`Qué significa ${titulo}`}
        aria-describedby={pos ? id : undefined}
        onMouseEnter={ubicar}
        onMouseLeave={cerrar}
        onFocus={ubicar}
        onBlur={cerrar}
        onKeyDown={(e) => e.key === 'Escape' && cerrar()}
        // Un clic también lo abre: en una pantalla táctil no hay `mouseenter`.
        onClick={(e) => {
          e.stopPropagation();
          pos ? cerrar() : ubicar();
        }}
        className={cn(
          'inline-flex items-center gap-1 align-middle text-ink-faint',
          'transition-colors hover:text-ink-strong focus-visible:text-ink-strong',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-ring focus-visible:ring-offset-1',
          'rounded',
          className,
        )}
      >
        {children}
        <HelpCircle className="h-3 w-3 shrink-0" aria-hidden />
      </button>

      {pos &&
        createPortal(
          <div
            ref={globo}
            id={id}
            role="tooltip"
            style={{
              top: pos.top,
              left: pos.left,
              width: ANCHO,
              transform: pos.abajo ? undefined : 'translateY(-100%)',
            }}
            className={cn(
              // Sin `pointer-events-none`: además de dejar seleccionar el texto, es lo que
              // permite comprobar que el globo NO está tapado — un elemento transparente al
              // hit-testing devuelve siempre lo de abajo y el chequeo no probaría nada.
              'fixed z-50 rounded-xl border border-hairline bg-panel p-3 shadow-alta',
              // ⚠️ NADA de `animate-fade-in` acá. Esa animación va con `both` y termina en
              // `transform: none`, y una animación le GANA a la declaración normal: se comía
              // el `translateY(-100%)` con el que el globo se apoya arriba del disparador, y
              // aparecía corrido hacia abajo. Es el gotcha del monorepo sobre animaciones con
              // `fill: both` pisando un transform, esta vez sobre un estilo inline.
            )}
          >
            <p className="text-xs font-semibold text-ink-strong">{titulo}</p>
            <p className="mt-1 text-2xs leading-relaxed text-ink-muted">{texto}</p>
          </div>,
          contenedor(),
        )}
    </>
  );
}
